// core/sound — memutar nada bel notifikasi (WebAudio, tanpa berkas suara).
//
// Pilihan nadanya per-PERANGKAT (localStorage), bukan per-akun di database: satu HP dipakai satu
// orang, dan pilihan suara bukan data usaha yang perlu ikut berpindah komputer.
import { NADA, nadaOf, nadaValid, type Nada } from '@domain/nada';

const KUNCI = 'ifics-nada';

export const nadaSaya = (): string => {
  try { return nadaValid(localStorage.getItem(KUNCI)); } catch { return nadaValid(null); }
};
export const setNadaSaya = (id: string): void => {
  try { localStorage.setItem(KUNCI, nadaValid(id)); } catch { /* mode privat — pilihan berlaku sesi ini saja */ }
};
export { NADA };

let ctx: AudioContext | null = null;

const buatCtx = (): AudioContext | null => {
  const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return null;
  ctx ??= new AC();
  return ctx;
};

/**
 * "Buka" jalur suara pada sentuhan/klik PERTAMA pengguna.
 *
 * Peramban melarang suara dari AudioContext yang dibuat di luar gesture. Sebelumnya context baru
 * dibuat saat notifikasi pertama tiba — bukan saat pengguna menyentuh halaman — sehingga bunyinya
 * dilewati dan TAK PERNAH terdengar sampai orangnya kebetulan menekan tombol Coba lebih dulu
 * (dilaporkan Apo: "notifikasi suara tidak terdengar ketika ada request").
 *
 * Dipasang sekali oleh shell. Bunyi hening sepanjang 1 sampel dipakai untuk mengunci status
 * 'running'; tanpa memutar apa pun, sebagian peramban membiarkannya tetap 'suspended'.
 */
export function siapkanSuara(): void {
  const buka = (): void => {
    const c = buatCtx();
    if (!c) return;
    void c.resume().catch(() => { /* diabaikan */ });
    try {
      const s = c.createBufferSource();
      s.buffer = c.createBuffer(1, 1, c.sampleRate);
      s.connect(c.destination);
      s.start(0);
    } catch { /* tak apa — resume() saja sudah cukup di banyak peramban */ }
    window.removeEventListener('pointerdown', buka);
    window.removeEventListener('keydown', buka);
  };
  window.addEventListener('pointerdown', buka, { once: false });
  window.addEventListener('keydown', buka, { once: false });
}

/**
 * Bunyikan satu nada. `id` kosong = pakai pilihan pengguna.
 *
 * Peramban MELARANG suara sebelum pengguna pernah menyentuh halaman (kebijakan autoplay). Karena
 * itu kegagalan di sini ditelan diam-diam: notifikasinya sendiri sudah masuk ke lonceng, dan
 * melempar galat hanya akan mengubah "tak berbunyi" menjadi "layar error".
 */
export function bunyikan(id?: string): void {
  try {
    const n: Nada = nadaOf(id ?? nadaSaya());
    if (!n.deret.length) return;                                   // 'mati'
    const c = buatCtx();
    if (!c) return;
    // Selama context belum 'running' (kebijakan autoplay peramban: SELALU begitu sebelum pengguna
    // menyentuh halaman), `currentTime` beku — oscillator yang dijadwalkan tetap menumpuk di detik
    // yang sama, lalu MELEDAK serentak begitu pengguna mengklik apa pun. Lebih baik lewatkan.
    if (c.state !== 'running') { void c.resume().catch(() => { /* diabaikan */ }); return; }
    const t0 = c.currentTime;
    for (const [hz, mulai, panjang] of n.deret) {
      const osc = c.createOscillator();
      const gain = c.createGain();
      osc.type = 'sine';
      osc.frequency.value = hz;
      // Naik-turun halus: gelombang sinus yang dipotong mendadak berbunyi "klik" di pengeras suara HP.
      gain.gain.setValueAtTime(0.0001, t0 + mulai);
      gain.gain.exponentialRampToValueAtTime(0.18, t0 + mulai + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + mulai + panjang);
      osc.connect(gain).connect(c.destination);
      osc.start(t0 + mulai);
      osc.stop(t0 + mulai + panjang + 0.02);
    }
  } catch { /* suara adalah pelengkap; lonceng tetap jalan tanpanya */ }
}
